import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import DatasetIcon from "@mui/icons-material/Dataset";
import axios from "../api/axios";
import { hasAnyRole } from "../utils/auth";

const DEFAULT_ROLE_OPTIONS = ["general", "faculty", "hod", "hr", "admin", "super_admin", "auditor"];
// Only super_admin can never be deleted; every other role can be removed and
// recreated manually by a super_admin.
const PROTECTED_ROLES = ["super_admin"];

const sectionLabelMap = {
  personal_information: "Personal Info",
  experience: "Experience",
  education: "Education",
  academic_information: "Academic",
  research_and_consultancy: "Research",
  fdp_and_training: "FDP / Training",
  awards_and_industry: "Award / Industry",
  appraisal: "Appraisal",
  faculty_dashboard: "Faculty Dashboard",
};

// Feature-flag sections that only have a show/hide (View) toggle — no verify/edit.
const VIEW_ONLY_SECTIONS = ["faculty_dashboard"];

function RoleAccessManagement() {
  const token = localStorage.getItem("token");
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [savingUserId, setSavingUserId] = useState(null);
  const [sectionRoles, setSectionRoles] = useState([]);
  const [sectionKeys, setSectionKeys] = useState([]);
  const [sectionMatrix, setSectionMatrix] = useState({});
  const [savingSections, setSavingSections] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [userRoleOptions, setUserRoleOptions] = useState(DEFAULT_ROLE_OPTIONS);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleType, setNewRoleType] = useState("global");
  const [newRoleDept, setNewRoleDept] = useState("");
  const [roleMetadataList, setRoleMetadataList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [creatingRole, setCreatingRole] = useState(false);
  const [deletingRole, setDeletingRole] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [appraisalEnabled, setAppraisalEnabled] = useState(true);
  const [savingAppraisalToggle, setSavingAppraisalToggle] = useState(false);

  const isSuperAdmin = hasAnyRole("super_admin");

  const loadUsers = useCallback(async () => {
    setError("");
    try {
      const res = await axios.get("/hr/access/users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers((res.data.users || []).map((user) => ({ ...user })));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load users");
    }
  }, [token]);

  const loadSectionVisibility = useCallback(async () => {
    setError("");
    try {
      const res = await axios.get("/hr/access/sections", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSectionRoles(res.data.roles || []);
      setSectionKeys(res.data.sections || []);
      setSectionMatrix(res.data.matrix || {});
      setRoleMetadataList(res.data.roleMetadata || []);

      const dynamicRoles = Array.from(new Set([...DEFAULT_ROLE_OPTIONS, ...(res.data.roles || [])]));
      setUserRoleOptions(dynamicRoles);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load section visibility");
    }
  }, [token]);

  const loadDepartments = useCallback(async () => {
    try {
      const res = await axios.get("/hr/departments", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(res.data.departments || []);
    } catch (err) {
      console.error("Failed to load departments:", err);
    }
  }, [token]);

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }

    loadUsers();
    loadSectionVisibility();
    loadDepartments();
    // Load the current appraisal visibility toggle.
    axios
      .get("/appraisal/settings", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setAppraisalEnabled(res.data?.appraisal_enabled !== false))
      .catch(() => {});
  }, [isSuperAdmin, loadUsers, loadSectionVisibility, loadDepartments, token]);

  const handleToggleAppraisal = async (enabled) => {
    setSavingAppraisalToggle(true);
    setError("");
    setSuccess("");
    // optimistic
    setAppraisalEnabled(enabled);
    try {
      await axios.put(
        "/appraisal/settings",
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Appraisal ${enabled ? "enabled — visible to everyone" : "hidden — no one can see or apply"}.`);
    } catch (err) {
      setAppraisalEnabled(!enabled); // revert
      setError(err.response?.data?.message || "Failed to update appraisal setting");
    } finally {
      setSavingAppraisalToggle(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) {
      return users;
    }

    return users.filter((user) => {
      const name = user.full_name || "";
      const email = user.email || "";
      const roleName = user.role || "";
      return (
        name.toLowerCase().includes(search) ||
        email.toLowerCase().includes(search) ||
        roleName.toLowerCase().includes(search)
      );
    });
  }, [users, query]);

  const updateLocalUser = (userId, field, value) => {
    setUsers((prev) =>
      prev.map((user) => {
        if (user.id === userId) {
          const updated = { ...user, [field]: value };
          // Super admin is always a moderator.
          if (field === "roles" && Array.isArray(value) && value.includes("super_admin")) {
            updated.is_moderator = true;
          }
          if (field === "role" && value === "super_admin") {
            updated.is_moderator = true;
          }
          return updated;
        }
        return user;
      })
    );
  };

  const toggleSectionPermission = (targetRole, sectionKey, permissionType, checked) => {
    setSectionMatrix((prev) => {
      const cell = prev[targetRole]?.[sectionKey] || { can_view: false, can_verify: false, can_edit: false };
      const updatedCell = typeof cell === "object" 
        ? { ...cell, [permissionType]: checked } 
        : { can_view: checked, can_verify: false, can_edit: false };

      return {
        ...prev,
        [targetRole]: {
          ...(prev[targetRole] || {}),
          [sectionKey]: updatedCell,
        },
      };
    });
  };

  const handleCreateRole = async () => {
    const trimmed = newRoleName.trim().toLowerCase();
    if (!trimmed) {
      setError("Please enter a valid role name");
      return;
    }

    setCreatingRole(true);
    setError("");
    setSuccess("");

    try {
      await axios.post(
        "/hr/access/roles",
        {
          role: trimmed,
          role_type: newRoleType,
          department_id: newRoleType === "department_specific" && newRoleDept ? Number(newRoleDept) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Role '${trimmed}' created successfully`);
      setNewRoleName("");
      setNewRoleType("global");
      setNewRoleDept("");
      loadSectionVisibility();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create role");
    } finally {
      setCreatingRole(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (PROTECTED_ROLES.includes(role)) {
      setError(`The '${role}' role cannot be deleted`);
      return;
    }
    const ok = window.confirm(
      `Delete the role "${role}"? It will be removed from the permissions matrix and from any user who currently has it.`
    );
    if (!ok) return;

    setDeletingRole(role);
    setError("");
    setSuccess("");
    try {
      await axios.delete(`/hr/access/roles/${encodeURIComponent(role)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(`Role '${role}' deleted successfully`);
      loadSectionVisibility();
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete role");
    } finally {
      setDeletingRole(null);
    }
  };

  const handleDeleteAllRoles = async () => {
    const ok = window.confirm(
      "Delete ALL roles except super_admin? Every other role (including built-ins) will be removed from the matrix and from all users, and affected users reset to 'general'. You will recreate roles manually. Continue?"
    );
    if (!ok) return;
    const confirmText = window.prompt('Type "DELETE" to confirm wiping all roles except super_admin.');
    if (confirmText !== "DELETE") {
      setError("Delete-all cancelled (confirmation text did not match).");
      return;
    }

    setDeletingAll(true);
    setError("");
    setSuccess("");
    try {
      await axios.delete("/hr/access/roles", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess("All roles except super_admin have been deleted.");
      loadSectionVisibility();
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete all roles");
    } finally {
      setDeletingAll(false);
    }
  };

  const savePreferences = async (user) => {
    setSavingUserId(user.id);
    setError("");
    setSuccess("");

    try {
      const rolesToSave = Array.isArray(user.roles) && user.roles.length
        ? user.roles
        : (user.role ? [user.role] : []);
      await axios.put(
        `/hr/access/users/${user.id}/preferences`,
        {
          roles: rolesToSave,
          is_moderator: !!user.is_moderator,
          is_active: !!user.is_active,
          department_id: user.department_id || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess("User preferences updated successfully");
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update user preferences");
    } finally {
      setSavingUserId(null);
    }
  };

  const saveSectionMatrix = async () => {
    setSavingSections(true);
    setError("");
    setSuccess("");

    try {
      await axios.put(
        "/hr/access/sections",
        { matrix: sectionMatrix },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess("Section visibility matrix saved successfully");
      loadSectionVisibility();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save section visibility matrix");
    } finally {
      setSavingSections(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Roles and Access Management
        </Typography>
        <Alert severity="error">Only super admin can access this page.</Alert>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={4}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center" mb={1}>
        <SecurityIcon color="primary" />
        <Typography variant="h5" fontWeight={800}>
          Roles and Access Management
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2.5}>
        Configure role access to data sections and manage user-level preferences from one place.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        sx={{ mb: 2 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab icon={<ManageAccountsIcon />} iconPosition="start" label="User Preferences" />
        <Tab icon={<DatasetIcon />} iconPosition="start" label="Section Visibility" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <TextField
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search users by name, email, role"
            sx={{ mb: 2 }}
          />

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 620 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Moderator</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{user.full_name || "N/A"}</Typography>
                      <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={user.department_code || "-"} />
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id={`role-label-${user.id}`}>Roles</InputLabel>
                        <Select
                          multiple
                          labelId={`role-label-${user.id}`}
                          label="Roles"
                          value={Array.isArray(user.roles) && user.roles.length ? user.roles : (user.role ? [user.role] : [])}
                          onChange={(e) =>
                            updateLocalUser(
                              user.id,
                              "roles",
                              typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value
                            )
                          }
                          renderValue={(selected) => (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {selected.map((r) => (
                                <Chip key={r} label={r} size="small" />
                              ))}
                            </Box>
                          )}
                        >
                          {userRoleOptions.map((roleOption) => (
                            <MenuItem key={roleOption} value={roleOption}>
                              {roleOption}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const rolesArr = Array.isArray(user.roles) && user.roles.length ? user.roles : (user.role ? [user.role] : []);
                        const isSuper = rolesArr.includes("super_admin");
                        return (
                          <Switch
                            checked={isSuper ? true : !!user.is_moderator}
                            disabled={isSuper}
                            onChange={(e) => updateLocalUser(user.id, "is_moderator", e.target.checked)}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!user.is_active}
                        onChange={(e) => updateLocalUser(user.id, "is_active", e.target.checked)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        size="small"
                        disabled={savingUserId === user.id}
                        onClick={() => savePreferences(user)}
                      >
                        {savingUserId === user.id ? "Saving" : "Save"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {/* Appraisal visibility toggle */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3.5, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <div>
                <Typography variant="subtitle1" fontWeight={700}>
                  📋 Appraisal Visibility
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When ON, faculty can see and apply for appraisal, and evaluators can review it.
                  When OFF, the appraisal section is hidden for everyone.
                </Typography>
              </div>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" fontWeight={600} color={appraisalEnabled ? "success.main" : "text.secondary"}>
                  {appraisalEnabled ? "Shown" : "Hidden"}
                </Typography>
                <Switch
                  color="success"
                  checked={appraisalEnabled}
                  disabled={savingAppraisalToggle}
                  onChange={(e) => handleToggleAppraisal(e.target.checked)}
                />
              </Stack>
            </Stack>
          </Paper>

          {/* Create Custom Role Card */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              🛠 Create Custom Role
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Create a custom moderation/moderator role. Once created, you can configure its view and verify permissions in the matrix below.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
              <TextField
                size="small"
                label="Role Name"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="e.g. cs_moderator, research_verifier"
                fullWidth
                sx={{ maxWidth: 250 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="role-type-label">Role Type</InputLabel>
                <Select
                  labelId="role-type-label"
                  label="Role Type"
                  value={newRoleType}
                  onChange={(e) => setNewRoleType(e.target.value)}
                >
                  <MenuItem value="global">Global (sees everyone)</MenuItem>
                  <MenuItem value="department_specific">Department specific (own department)</MenuItem>
                  <MenuItem value="individual">Individual (self only)</MenuItem>
                </Select>
              </FormControl>
              {newRoleType === "department_specific" && (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="role-dept-label">Department</InputLabel>
                  <Select
                    labelId="role-dept-label"
                    label="Department"
                    value={newRoleDept}
                    onChange={(e) => setNewRoleDept(e.target.value)}
                  >
                    <MenuItem value="">Select…</MenuItem>
                    {departments.map((d) => (
                      <MenuItem key={d.id} value={d.id}>{d.name} ({d.code})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Button
                variant="contained"
                onClick={handleCreateRole}
                disabled={creatingRole}
              >
                {creatingRole ? "Creating..." : "Create Role"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDeleteAllRoles}
                disabled={deletingAll}
              >
                {deletingAll ? "Deleting..." : "Delete All Roles"}
              </Button>
            </Stack>
          </Paper>

          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            Role to Section Permissions Matrix
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            Configure whether each role can **View**, **Verify (Moderate)**, and **Edit** details inside each section. Turning on Edit lets that role edit other users' details. Super admin retains full permissions.
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  {sectionKeys.map((sectionKey) => (
                    <TableCell key={sectionKey} align="center">
                      {sectionLabelMap[sectionKey] || sectionKey}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sectionRoles.map((sectionRole) => (
                  <TableRow key={sectionRole} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        color={sectionRole === "super_admin" ? "primary" : "default"}
                        label={sectionRole}
                      />
                      {(() => {
                        const meta = roleMetadataList.find((m) => m.role === sectionRole);
                        if (!meta) return null;
                        const typeLabel = meta.role_type === "branch_specific"
                          ? "Department Specific"
                          : "Global";
                        return (
                          <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                            {typeLabel}
                          </Typography>
                        );
                      })()}
                      {!PROTECTED_ROLES.includes(sectionRole) && (
                        <Button
                          size="small"
                          color="error"
                          variant="text"
                          disabled={deletingRole === sectionRole}
                          onClick={() => handleDeleteRole(sectionRole)}
                          sx={{ mt: 0.5, minWidth: 0, p: 0, textTransform: "none" }}
                        >
                          {deletingRole === sectionRole ? "Deleting..." : "🗑 Delete role"}
                        </Button>
                      )}
                    </TableCell>
                    {sectionKeys.map((sectionKey) => {
                      const cellConfig = sectionMatrix?.[sectionRole]?.[sectionKey] || { can_view: false, can_verify: false, can_edit: false };
                      const canView = typeof cellConfig === "object" ? !!cellConfig.can_view : !!cellConfig;
                      const canVerify = typeof cellConfig === "object" ? !!cellConfig.can_verify : false;
                      const canEdit = typeof cellConfig === "object" ? !!cellConfig.can_edit : false;

                      // Feature flags like the Faculty Dashboard are view-only:
                      // there is nothing to "verify" or "edit", just show/hide.
                      const viewOnly = VIEW_ONLY_SECTIONS.includes(sectionKey);

                      return (
                        <TableCell key={`${sectionRole}-${sectionKey}`} align="center">
                          <Stack spacing={1.2} alignItems="center" py={1}>
                            <Stack direction="row" spacing={0.6} alignItems="center">
                              <Typography variant="caption" fontWeight={600} color="text.secondary">View:</Typography>
                              <Switch
                                size="small"
                                checked={canView}
                                onChange={(e) => toggleSectionPermission(sectionRole, sectionKey, "can_view", e.target.checked)}
                                disabled={sectionRole === "super_admin"}
                              />
                            </Stack>
                            {!viewOnly && (
                              <>
                                <Stack direction="row" spacing={0.6} alignItems="center">
                                  <Typography variant="caption" fontWeight={600} color="text.secondary">Verify:</Typography>
                                  <Switch
                                    size="small"
                                    color="success"
                                    checked={canVerify}
                                    onChange={(e) => toggleSectionPermission(sectionRole, sectionKey, "can_verify", e.target.checked)}
                                    disabled={sectionRole === "super_admin"}
                                  />
                                </Stack>
                                <Stack direction="row" spacing={0.6} alignItems="center">
                                  <Typography variant="caption" fontWeight={600} color="text.secondary">Edit:</Typography>
                                  <Switch
                                    size="small"
                                    color="warning"
                                    checked={canEdit}
                                    onChange={(e) => toggleSectionPermission(sectionRole, sectionKey, "can_edit", e.target.checked)}
                                    disabled={sectionRole === "super_admin"}
                                  />
                                </Stack>
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="flex-end" mt={2.5}>
            <Button
              variant="contained"
              size="large"
              onClick={saveSectionMatrix}
              disabled={savingSections}
            >
              {savingSections ? "Saving" : "Save Section Permissions"}
            </Button>
          </Stack>
        </Box>
      )}
    </Paper>
  );
}

export default RoleAccessManagement;
